<template>
  <div class="compliance">
    <!-- Compliance Filters -->
    <filters-section 
      :showLocation="true" 
      :defaultValues="global.filterDefaults" 
      :filterDisplayKeys="getFilterKeys()" 
      v-on:change="formatFilters">
    </filters-section>

    <!-- Compliance Table -->
    <page-section 
      title="Compliance Action Plans" 
      v-loading="complianceSection.loading">
      <template slot="title">
        <div class="compliance-filter">
          <el-button data-testid="new-action-plan" 
            class="compliance-filter-button" 
            :disabled="!canModify" 
            :plain="true" 
            type="primary" 
            size="mini" 
            @click="createActionPlan()">
            + New Action Plan
          </el-button>
          <el-select 
            class="compliance-filter-select" 
            size="mini" 
            v-model="complianceSection.sortBy">
            <el-option 
              v-for="item in global.sortByOptions" 
              :key="item.value" 
              :label="item.label" 
              :value="item.value">
            </el-option>
          </el-select>
          <el-pagination
            @size-change="(size) => { complianceSection.pagination.size = size }"
            @current-change="(page) => { complianceSection.pagination.page = page }"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="complianceSection.pagination.size"
            layout="sizes, prev, pager, next"
            :total="complianceSection.pagination.total">
          </el-pagination>
        </div>
      </template>

      <el-table :data="complianceSection.data">
          <el-table-column prop="name" label="Title" width="200">
              <template slot-scope="scope">
                  <a @click="viewActionPlan(scope.row)">{{ scope.row.name }}</a>
              </template>
          </el-table-column>
          <el-table-column prop="facilityName" label="Facility" width="140"></el-table-column>
          <el-table-column prop="createdBy" label="Created By" width="140"></el-table-column>
          <el-table-column prop="created" label="Created Date" width="140">
              <template slot-scope="scope">
                  {{ formatDate(scope.row.created) }}
              </template>
          </el-table-column>
          <el-table-column prop="updatedBy" label="Updated By" width="140"></el-table-column>
          <el-table-column prop="updated" label="Updated Date" width="140">
              <template slot-scope="scope">
                  {{ formatDate(scope.row.updated) }}
              </template>
          </el-table-column>
          <el-table-column prop="assignedTo" label="Assigned To" width="140">
              <template slot-scope="scope">
                  {{ scope.row.assignedTo || 'Unassigned' }}
              </template>
          </el-table-column>
          <el-table-column prop="status" label="Status" width="120">
              <template slot-scope="scope">
                  <el-tag :type="getStatusType(scope.row.status)">
                      {{ scope.row.status ? 'Active' : 'Inactive' }}
                  </el-tag>
              </template>
          </el-table-column>
          <el-table-column prop="progress" label="Progress" width="140">
              <template slot-scope="scope">
                  <el-tag :type="getProgressType(scope.row.progress)">
                      {{ formatProgress(scope.row.progress) }}
                  </el-tag>
              </template>
          </el-table-column>
          <el-table-column prop="resolutionDate" label="Resolution Date" width="140">
              <template slot-scope="scope">
                  {{ scope.row.resolutionDate ? formatDate(scope.row.resolutionDate) : '-' }}
              </template>
          </el-table-column>
          <el-table-column prop="deficiencyCount" label="Deficiencies" width="120">
              <template slot-scope="scope">
                  {{ scope.row.deficiencyCount || 0 }}
              </template>
          </el-table-column>
          <el-table-column label="Actions" width="120" fixed="right">
              <template slot-scope="scope">
                  <el-button data-testid="download-action-plan" 
                  size="mini" 
                  type="text" 
                  @click="downloadPDF(scope.row)">
                  Download
                  </el-button>
                  <el-button data-testid="delete-action-plan" 
                  size="mini" 
                  type="text" 
                  @click="deleteActionPlan(scope.row)"
                  :disabled="!canModify">
                  Delete
                  </el-button>
              </template>
          </el-table-column>
      </el-table>
    </page-section>

    <!-- Create/Edit Action Plan Dialog -->
    <el-dialog
      :title="actionPlanDialog.title"
      :visible.sync="actionPlanDialog.visible"
      width="500px">
      <el-form :model="actionPlanDialog.form" label-width="120px">
        <el-form-item label="Title" required>
          <el-input v-model="actionPlanDialog.form.title"></el-input>
        </el-form-item>
        <el-form-item label="Facility" required>
          <el-select v-model="actionPlanDialog.form.facilityId" placeholder="Select Facility">
            <el-option
              v-for="facility in global.facilities"
              :key="facility.id"
              :label="facility.displayText"
              :value="facility.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Assigned To" required>
          <el-input v-model="actionPlanDialog.form.assignedTo"></el-input>
        </el-form-item>
        <el-form-item label="Status">
          <el-switch
          v-model="actionPlanDialog.form.status"
          active-text="Active"
          inactive-text="Inactive">
          </el-switch>
        </el-form-item>
        <el-form-item label="Progress">
          <el-select v-model="actionPlanDialog.form.progress">
          <el-option label="New" value="New"></el-option>
          <el-option label="In Progress" value="In Progress"></el-option>
          <el-option label="Under Review" value="Reviewing"></el-option>
          <el-option label="Completed" value="Completed"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="PDF Document" required>
          <el-upload
            class="upload-demo"
            action="#"
            :auto-upload="false"
            :on-change="handleFileChange"
            :file-list="actionPlanDialog.fileList"
            accept="application/pdf">
            <el-button data-testid="select-file" size="small" type="primary">Select File</el-button>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button data-testid="cancel-action-plan" @click="actionPlanDialog.visible = false">Cancel</el-button>
        <el-button data-testid="create-action-plan" type="primary" @click="submitActionPlan">Create</el-button>
      </span>
    </el-dialog>

    <!-- Error Dialog -->
    <el-dialog
      title="Error"
      :visible.sync="errorDialog.visible"
      width="30%">
      <span>{{ errorDialog.message }}</span>
      <span slot="footer" class="dialog-footer">
        <el-button data-testid="dismiss-error" type="primary" @click="errorDialog.visible = false">OK</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import auth from "../../auth";
import FiltersSection from "../Shared/FiltersSection";
import PageSection from "../Shared/PageSection";
import moment from "moment";
import html2pdf from 'html2pdf.js';

export default {
  name: "compliance",
  components: {
    "filters-section": FiltersSection,
    "page-section": PageSection
  },
  data() {
    return {
      canModify: false,
      global: {
        facilities: [],
        filters: {},
        filterDefaults: {},
        sortByOptions: [
          {
            label: "Sort by Created Date",
            value: "created"
          },
          {
              label: "Sort by Updated Date",
              value: "updated"
          },
          {
            label: "Sort by Title",
            value: "name"
          },
          {
            label: "Sort by Progress",
            value: "progress"
          }
        ]
      },
      complianceSection: {
        loading: false,
        data: [],
        sortBy: "created",
        pagination: {
          page: 1,
          size: 10,
          total: 0
        }
      },
      actionPlanDialog: {
          visible: false,
          title: "Create New Action Plan",
          form: {
              title: "",
              file: null,
              facilityId: "",
              assignedTo: "",
              status: true,
          }
      },
      errorDialog: {
        visible: false,
        message: ""
      }
    };
  },
  watch: {
    "complianceSection.pagination.size"() {
      this.getActionPlans();
    },
    "complianceSection.pagination.page"() {
      this.getActionPlans();
    },
    "complianceSection.sortBy"() {
      this.getActionPlans();
    }
  },
  methods: {
    getFilterKeys() {
      return ["created", "facility", "status", "assignedTo", "progress"];
    },
    formatFilters(rawFilters) {
      const formattedFilters = {
          facility: rawFilters.facility ? Number(rawFilters.facility) : undefined,
          progress: rawFilters.progress,
          status: rawFilters.status,
          created: rawFilters.created,
          assignedTo: rawFilters.assignedTo,
          useCurrentLocation: true
      };
      this.global.filters = formattedFilters;
      this.getActionPlans();
    },
    async getActionPlans() {
      this.complianceSection.loading = true;
      try {
          const payload = {
          page: this.complianceSection.pagination.page,
          size: this.complianceSection.pagination.size,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          orderBy: this.complianceSection.sortBy,
          ...this.global.filters
          };

          const response = await this.$http.post(`${window.CONFIG.compliance_api}/search`, payload);
          
          // Update pagination with the correct total
          this.complianceSection.pagination.total = response.body.totalElements;
          
          // Map the data and add facility names
          this.complianceSection.data = response.body.content.map(this.formatActionPlanData);
      } catch (error) {
          this.showError("Failed to load action plans");
      }
      this.complianceSection.loading = false;
    },
    formatActionPlanData(record) {
      return {
        ...record,
        facilityName: this.getFacilityName(record.facility)
      };
    },
    viewActionPlan(plan) {
      this.$router.push(`/compliance/action-plan/${plan.id}`);
    },
    getFacilityName(facId) {
      const facility = this.global.facilities.find(f => f.id === Number(facId));
      return facility ? facility.displayText : "";
    },
    formatDate(date) {
      return moment(date).format("MM/DD/YYYY");
    },
    getStatusType(status) {
      return status ? 'success' : 'danger';
    },
    getProgressType(progress) {
      const types = {
          'NEW': 'info',
          'IN_PROGRESS': 'warning',
          'UNDER_REVIEW': 'primary',
          'COMPLETED': 'success'
      };
      return types[progress] || 'info';
    },
    formatProgress(progress) {
      if (!progress) return 'Unknown';
      return progress?.toLowerCase()
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ') || 'Unknown';
    },
    createActionPlan() {
      this.actionPlanDialog.title = "Create New Action Plan";
      this.actionPlanDialog.form = {
        title: "",
        facilityId: "",
        assignedTo: "",
        status: true,
        progress: "New"
      };
      this.actionPlanDialog.fileList = [];
      this.actionPlanDialog.visible = true;
    },
    handleFileChange(file) {
      if (file && file.raw instanceof File) {
          this.actionPlanDialog.form.file = file.raw;
      } else {
          console.error("Invalid file selected", file);
          this.showError("Please select a valid PDF file.");
      }
    },
    async submitActionPlan() {
      if (!this.actionPlanDialog.form.file) {
          this.showError("Please select a PDF file");
          return;
      }
      try {
          const filename = this.actionPlanDialog.form.file.name;

          // Request the presigned URL from the backend
          const response = await this.$http.post(
          `${window.CONFIG.compliance_api}/uploadtos3/${filename}`, // Backend route to get presigned URL
          {},
          { headers: { "Content-Type": "application/json" } }
          );

          if (response.data.uploadUrl) {
          const { uploadUrl, key } = response.data;

          const uploadResponse = await fetch(uploadUrl, {
              method: "PUT",
              headers: {
              "Content-Type": "application/pdf"
              },
              body: this.actionPlanDialog.form.file
          });

          if (!uploadResponse.ok) {
              throw new Error("File upload to S3 failed");
          }

          const formPayload = {
              name: this.actionPlanDialog.form.title,
              facility: this.actionPlanDialog.form.facilityId,
              assignedTo: this.actionPlanDialog.form.assignedTo,
              status: this.actionPlanDialog.form.status,
              progress: this.actionPlanDialog.form.progress,
              file: key // S3 file key
          };

          // Create the action plan
          await this.$http.post(`${window.CONFIG.compliance_api}`, formPayload, {
              headers: { "Content-Type": "application/json" }
          });

          // Close the dialog and refresh the action plans list
          this.actionPlanDialog.visible = false;
          this.getActionPlans();
          } else {
          throw new Error("Failed to get the upload URL");
          }
      } catch (error) {
          console.error("Error creating action plan:", error);
          this.showError("Failed to create action plan");
      }
    },
    async deleteActionPlan(plan) {
      try {
        await this.$confirm("Are you sure you want to delete this action plan?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          type: "warning"
        });
        
        await this.$http.delete(`${window.CONFIG.compliance_api}/${plan.id}`);
        this.getActionPlans();
      } catch (error) {
        if (error !== "cancel") {
          this.showError("Failed to delete action plan");
        }
      }
    },
    async downloadPDF(plan) {
      try {
        // 1. Fetch full report
        const url = `${window.CONFIG.compliance_api}/${plan.id}`;
        const response = await this.$http.get(url);
        const actionPlan = response.data.report;

        // 2. Create container with editor content and styles
        const element = document.createElement('div');
        element.innerHTML = actionPlan.actionplantext;

        const style = document.createElement('style');
        style.textContent = `
          @media print {
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            table {
              page-break-inside: avoid;
            }
            img {
              page-break-inside: avoid;
            }
            p {
              orphans: 3;
              widows: 3;
            }
          }
        `;
        element.appendChild(style);

        const title = document.createElement('h1');
        title.textContent = actionPlan.name;
        title.style.textAlign = 'center';
        title.style.marginBottom = '20px';
        element.insertBefore(title, element.firstChild);

        const metadata = document.createElement('div');
        metadata.innerHTML = `
          <p style="text-align: right; margin-bottom: 20px;">
            Facility: ${this.getFacilityName(actionPlan.facility)}<br>
            Date: ${moment().format('MMMM D, YYYY')}
          </p>
        `;
        element.insertBefore(metadata, element.firstChild);

        const opt = {
          margin: 1,
          filename: `action-plan-${plan.id}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false
          },
          jsPDF: {
            unit: 'in',
            format: 'letter',
            orientation: 'portrait'
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        await html2pdf().set(opt).from(element).save();
        this.$message.success('PDF downloaded successfully');
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.showError('Failed to generate PDF');
      }
    },
  },
  created() {
      this.canModify = auth.canModify();
      this.global.facilities = this.$configStore.data.sites;
      // Set initial filters
      this.global.filterDefaults = {
      status: true,
      useCurrentLocation: true
      };
      this.getActionPlans();
  }
};
</script>

<style>
.compliance-filter > .el-pagination {
  padding-top: 1px;
  float: right;
}

.compliance-filter > .compliance-filter-select {
  width: 160px;
  margin-right: 1em;
}

.compliance-filter > .compliance-filter-button {
  float: left;
  margin-right: 1em;
  margin-top: 1px;
}

.compliance-filter {
  margin-right: 1em;
  margin-left: 3em;
  position: absolute;
  right: 0;
  top: 0.5em;
}

.el-progress {
  margin-top: 8px;
}
</style>